import {
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs,
  query,
  where,
  DocumentReference,
} from "firebase/firestore";
import { CampLocation } from "@/types/campLocation";
import {
  computeCampSlugPathForLocation,
  normalizeSlugSegment,
  resolveEffectiveCanonicalLocationId,
} from "@/functions-shared/campPublicSlugPath";
import { resolveOrgIdFromProvidedByOrg } from "@/functions-shared/resolveOrgIdFromProvidedByOrg";

function orgRefForId(db: Firestore, orgId: string): DocumentReference {
  return doc(db, "campOrganizations", orgId);
}

/**
 * Computes `slugPath` for denormalizing onto `offers.locationData.slugPath` (and matching Cloud Functions / `campPublicRoutes` semantics).
 * Robust to missing `campLocations.slugPath` (recomputes from org + siblings).
 */
export async function resolveCampSlugPathForOffer(
  db: Firestore,
  locationId: string,
  locationData: CampLocation
): Promise<string> {
  const orgId = resolveOrgIdFromProvidedByOrg((locationData as CampLocation).providedByOrg as unknown);
  if (!orgId) return "";

  const orgSnap = await getDoc(doc(db, "campOrganizations", orgId));
  if (!orgSnap.exists()) return "";

  const orgSlugNorm = normalizeSlugSegment(orgSnap.get("slug"));
  const canonicalField = orgSnap.get("canonicalLocationId");

  const sibQ = query(
    collection(db, "campLocations"),
    where("providedByOrg", "==", orgRefForId(db, orgId))
  );
  const sibSnap = await getDocs(sibQ);
  const orgLocationIds = sibSnap.docs.map((d) => d.id);
  const effectiveCanon = resolveEffectiveCanonicalLocationId(canonicalField, orgLocationIds);

  const locSlugNorm = normalizeSlugSegment(locationData.slug);
  return computeCampSlugPathForLocation({
    orgSlugNormalized: orgSlugNorm,
    locationId,
    locationSlugNormalized: locSlugNorm,
    effectiveCanonicalLocationId: effectiveCanon,
  });
}
